import React, { createContext, useState, useContext, useEffect, useCallback, type ReactNode } from 'react';
import { api } from '@/api/client';
import type { User } from '@/types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoadingAuth: boolean;
  authError: { type: string; message: string } | null;
  loginWithGoogle: (returnUrl?: string) => void;
  logout: () => Promise<void>;
  checkUserAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(true);
  const [authError, setAuthError] = useState<{ type: string; message: string } | null>(null);

  const checkUserAuth = useCallback(async () => {
    setIsLoadingAuth(true);
    try {
      const currentUser = await api.auth.me();
      setUser(currentUser);
      setIsAuthenticated(true);
      setAuthError(null);
    } catch (error: any) {
      setUser(null);
      setIsAuthenticated(false);
      if (error?.status === 401) {
        setAuthError({
          type: 'auth_required',
          message: 'Please sign in with Google to continue.',
        });
      }
    } finally {
      setIsLoadingAuth(false);
    }
  }, []);

  useEffect(() => {
    checkUserAuth();
  }, [checkUserAuth]);

  const loginWithGoogle = (returnUrl?: string): void => {
    api.auth.loginWithGoogle(returnUrl);
  };

  const logout = async (): Promise<void> => {
    try {
      await api.auth.logout();
    } catch (e) {
      console.error('Logout failed on server:', e);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/';
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoadingAuth,
        authError,
        loginWithGoogle,
        logout,
        checkUserAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
